"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  type FC,
} from "react"

interface DevModeContextValue {
  /** 開発モードが有効かどうか（NODE_ENV === "development" かつ isDevUIVisible） */
  isDevMode: boolean
  /** 開発UIの表示/非表示（ユーザーがトグル可能） */
  isDevUIVisible: boolean
  /** 開発UIの表示/非表示を切り替える */
  toggleDevUI: () => void
  /** 開発UIを表示する */
  showDevUI: () => void
  /** 開発UIを非表示にする */
  hideDevUI: () => void
}

const DevModeContext = createContext<DevModeContextValue | null>(null)

const DEV_UI_STORAGE_KEY = "devUIVisible"

/**
 * 開発モードの状態を管理するProvider
 * - NODE_ENV === "development" の場合のみ開発機能が有効
 * - isDevUIVisible で開発UIの表示/非表示を制御（localStorageに永続化）
 */
export const DevModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isDevUIVisible, setIsDevUIVisible] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(DEV_UI_STORAGE_KEY)
    // デフォルトはtrue（開発環境では表示）
    return stored === null ? true : stored === "true"
  })

  const isDevelopment = process.env.NODE_ENV === "development"

  const toggleDevUI = useCallback(() => {
    setIsDevUIVisible((prev) => {
      const next = !prev
      localStorage.setItem(DEV_UI_STORAGE_KEY, String(next))
      return next
    })
  }, [])

  const showDevUI = useCallback(() => {
    setIsDevUIVisible(true)
    localStorage.setItem(DEV_UI_STORAGE_KEY, "true")
  }, [])

  const hideDevUI = useCallback(() => {
    setIsDevUIVisible(false)
    localStorage.setItem(DEV_UI_STORAGE_KEY, "false")
  }, [])

  const value: DevModeContextValue = {
    isDevMode: isDevelopment && isDevUIVisible,
    isDevUIVisible,
    toggleDevUI,
    showDevUI,
    hideDevUI,
  }

  return (
    <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>
  )
}

/**
 * 開発モードの状態を取得するhook
 */
export const useDevMode = (): DevModeContextValue => {
  const context = useContext(DevModeContext)
  if (!context) {
    // Provider外で使用された場合はデフォルト値を返す
    return {
      isDevMode: false,
      isDevUIVisible: false,
      toggleDevUI: () => {},
      showDevUI: () => {},
      hideDevUI: () => {},
    }
  }
  return context
}
